/** Material UI Packages. */
import { Button } from '@mui/material';
import { GridToolbarContainer, GridRowModes } from '@mui/x-data-grid-pro';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';

/**
 * Recursively updates the 'id' property of an object and all nested objects with a new ID.
 *
 * @param {object} obj - The object whose 'id' values should be updated.
 * @param {string} newId - The new ID to assign to all 'id' fields.
 * @returns {object} A new object with updated IDs.
 */
function updateIdDeep(obj, newId) {
    if (typeof obj !== 'object' || obj === null) return obj;

    // Create a shallow copy of the object or array
    const updated = Array.isArray(obj) ? [...obj] : { ...obj };

    Object.keys(updated).forEach((key) => {
        if (key === 'id') {
            updated[key] = newId;
            // If a nested value is also an object, recursively invoke the function
        } else if (typeof updated[key] === 'object' && updated[key] !== null) {
            updated[key] = updateIdDeep(updated[key], newId);
        }
    });

    return updated;
}

/** Custom toolbar component for adding a new container row in the datagrid.
 *
 * @param {function} setRowModesModel - Setter for updating the row editing state in DataGrid.
 * @param {function} dispatch - Redux dispatch function to send actions.
 * @param {function} newReducer - The reducer action to call when adding a new row.
 * @param {object} newValue - The default object structure to be added to the grid.
 * @param {string} fieldToFocusValue - Field name to focus when new row is added in edit mode.
 * @param {string} title - Button label text.
 *
 * @returns {JSX.Element} Rendered toolbar with "Add" button.
 */
export default function EditToolbar({ setRowModesModel, dispatch, newReducer, newValue, fieldToFocusValue, title }) {
    /**
     * Handler for "Add" button click.
     * - Generates a new ID
     * - Updates the new row value with this ID
     * - Dispatches add action to Redux
     * - Sets the new row to edit mode in the grid
     */
    const handleClick = () => {
        const id = randomId();
        const updatedValue = updateIdDeep(newValue, id);
        dispatch(newReducer(updatedValue));
        // Put the new row into edit mode and focus the specified field
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus: fieldToFocusValue },
        }));
    };

    return (
        <GridToolbarContainer>
            <Button sx={{ color: '#003a52', fontSize: '11px' }} startIcon={<AddIcon />} onClick={handleClick}>
                {title}
            </Button>
        </GridToolbarContainer>
    );
}
